import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../../../components/User_Context.js';
import * as menu from "../../../../assets/menus/menu.js";
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import * as api from "../../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import { formBuilderLabels } from '../../../_label/form_builder/form_builder_labels.js';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Form_Compiler from '../compilerNew.js';
import {calculateHeaderHeight} from '../../../../components/ui_utils.js';

/**
 * LIS Checklist Dashboard
 * Shows only form assignments created from LIS workflow (infection alerts)
 * Filters assignments by ContextData.source === 'lis_infection_alert'
 */
const LIS_Checklist_Dashboard = () => {
    const { currentUser } = useContext(UserContext);
    const [assignments, setAssignments] = useState(null);
    const [activeTab, setActiveTab] = useState('Active'); // 'Active', 'Completed' or 'Approved'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);
    const [headerHeight, setHeaderHeight] = useState(0);
    
    // Form viewing state
    const [viewingForm, setViewingForm] = useState(null);
    const [viewingFormData, setViewingFormData] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(true);

    // Initialize translations and setup
    useEffect(() => {
        setHeaderHeight(calculateHeaderHeight());
        
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);

        menu.write({ user: currentUser() });

        // Set breadcrumb
        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/lis/checklist_dashboard", name: t('lisChecklistDashboard', { ns: 'labels', defaultValue: 'Dashboard Checklist LIS' }) }
        ]);

        loadDashboardData();
    }, []);

    // Load dashboard data for LIS checklists only
    const loadDashboardData = async (status = null) => {
        try {
            setLoading(true);
            setError(null);

            const body = {
                filterByLIS: true  // Flag to filter only LIS workflow assignments
            };
            if (status) {
                body.status = status;
            }

            const result = await api.sendMessage("darm/formAssignments/getLisDashboardData", {
                user: currentUser(),
                body: body
            });

            if (result && result.success) {
                setAssignments(result.assignments || []);
            } else {
                throw new Error(result?.error || 'Failed to load LIS dashboard data');
            }
        } catch (error) {
            console.error("Error loading LIS dashboard data:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter assignments based on active tab
    const getFilteredAssignments = () => {
        if (!assignments) return [];
        return assignments.filter(assignment => assignment.Status === activeTab);
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format date and time for display
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Saved': return 'bg-warning text-dark';
            case 'Compiled': return 'bg-info text-white';
            case 'Approved': return 'bg-success text-white';
            default: return 'bg-secondary text-white';
        }
    };

    // Navigate to form compilation
    const handleCompileForm = (assignment) => {
        // Check if there are remaining cycles
        if (assignment.remainingCycles <= 0) {
            MySwal.fire({
                title: t('attention', { ns: 'labels' }),
                text: t('allCyclesCompleted', { ns: 'labels' }),
                icon: 'warning'
            });
            return;
        }

        // Navigate to form compilation
        window.location.href = `/form/compile/${assignment.FormId}?assignmentId=${assignment.Id}`;
    };

    // Handle viewing a completed form
    const handleViewCompletion = async (assignment, completion) => {
        // For active assignments, check if the completion is "Compiled" (completed cycle)
        if (activeTab === 'Active') {
            // If the completion has status "Compiled" and counts as cycle, show in read-only mode
            if (completion.Status === 'Compiled' && completion.countsAsCycle) {
                // Fall through to show in read-only mode (same behavior as non-active assignments)
            }
            // If user has completed their part in a collaborative workflow, show in read-only mode
            else if (completion.workflowCompletionInfo &&
                     completion.workflowCompletionInfo.hasWorkflow &&
                     completion.workflowCompletionInfo.userHasCompleted &&
                     !completion.workflowCompletionInfo.needsUserAction) {
                // Fall through to show in read-only mode
            }
            else {
                // For other completions in active assignments (including "needs your action"), redirect to the compilation page
                window.location.href = `/form/compile/${assignment.FormId}/${completion.Id}?assignmentId=${assignment.Id}`;
                return;
            }
        }

        // For non-active assignments or completed cycles in active assignments, show in read-only mode (existing behavior)
        try {
            // Show loading indicator
            MySwal.fire({
                title: t('loading', { ns: 'labels' }),
                text: t('loadingFormData', { ns: 'labels', defaultValue: 'Loading form data...' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Load both the form completion data AND the form structure
            const [completionResult, formResult] = await Promise.all([
                // Load the form completion data
                api.sendMessage("darm/formCompletion/get", {
                    user: currentUser(),
                    body: {
                        formId: assignment.FormId,
                        completionId: completion.Id,
                        assignmentId: assignment.Id
                    }
                }),
                // Load the form structure
                api.sendMessage("darm/formBuilder/get", {
                    user: currentUser(),
                    body: {
                        id: assignment.FormId
                    }
                })
            ]);

            MySwal.close();

            if (completionResult?.success && completionResult?.formCompletion && formResult?.success && formResult?.form) {
                // Combine the form structure with the completion data
                const combinedData = {
                    ...completionResult.formCompletion,
                    form: formResult.form
                };
                
                console.log("🔍 [DEBUG] Combined form data:", combinedData);
                
                // Set read-only mode (always true for non-active assignments)
                setIsReadOnly(true);
                
                // Set the viewing form data
                setViewingForm(assignment);
                setViewingFormData(combinedData);
                
                // Update breadcrumb when viewing a form
                updateBreadcrumbForFormView(assignment.FormName || 'Form');
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: completionResult?.message || formResult?.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                    icon: 'error'
                });
            }
        } catch (error) {
            MySwal.close();
            console.error("Error loading form for viewing:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                icon: 'error'
            });
        }
    };

    // Handle closing the form viewer
    const handleCloseFormViewer = () => {
        setViewingForm(null);
        setViewingFormData(null);
        setIsReadOnly(true); // Reset to default read-only state
        // Reset breadcrumb to dashboard
        setBreadCrumb();
    };

    // Update breadcrumb when viewing a form
    const updateBreadcrumbForFormView = (formName) => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/lis/checklist_dashboard", name: t('lisChecklistDashboard', { ns: 'labels', defaultValue: 'Dashboard Checklist LIS' }) },
            { link: "#", name: `${t('viewingForm', { ns: 'labels', defaultValue: 'Viewing Form' })}: ${formName}` }
        ];
        breadcrumb.set(path);
    };

    // Set breadcrumb for the dashboard
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/lis/checklist_dashboard", name: t('lisChecklistDashboard', { ns: 'labels', defaultValue: 'Dashboard Checklist LIS' }) }
        ];
        breadcrumb.set(path);
    };



    // Assignment Card Component - Enhanced Grid Layout
    const AssignmentCard = ({ assignment }) => {
        const [expanded, setExpanded] = useState(false);

        /**
         * Get context information text for LIS infection alerts
         * Returns simple text display without badges
         */
        const getContextInfo = () => {
            // Check if ContextData exists and is from LIS infection alert
            if (!assignment.ContextData || assignment.ContextData.source !== 'lis_infection_alert') {
                return null;
            }

            const ctx = assignment.ContextData;
            const parts = [];

            // Add Business Unit
            if (ctx.businessUnit?.name) {
                parts.push(ctx.businessUnit.name);
            }

            // Add patient 3-letter code
            if (ctx.patient?.lastName) {
                const patientCode = ctx.patient.lastName.substring(0, 3).toUpperCase();
                parts.push(patientCode);
            }

            // Add microorganisms
            if (ctx.microorganisms && ctx.microorganisms.length > 0) {
                const microNames = ctx.microorganisms.map(mo => mo.name).join(', ');
                parts.push(microNames);
            } else if (ctx.microorganism?.name) {
                parts.push(ctx.microorganism.name);
            }

            // Add lis ID
            if (ctx.lisId) {
                parts.push(`ID Referto: ${ctx.lisId}`);
            }

            // Add test date
            if (ctx.test?.date) {
                parts.push(formatDate(ctx.test.date));
            }

            if (parts.length === 0) return null;

            return (
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {parts.join(' - ')}
                </small>
            );
        };

        return (
            <div className="card mb-2 shadow-sm" style={{
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s'
            }}>
                {/* Compact Header - Enhanced Grid Layout */}
                <div className="card-body p-2" style={{ fontSize: '0.75rem' }}>
                    <div className="row g-3 align-items-center">
                        {/* Column 1: Form Info (25%) */}
                        <div className="col-3">
                            <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>
                                    {assignment.FormName}
                                </h6>
                                {getContextInfo()}
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                    {assignment.FormCode && (
                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            <i className="fas fa-hashtag" style={{ fontSize: '0.6rem' }}></i> {assignment.FormCode}
                                        </span>
                                    )}
                                    {assignment.FormGroup && (
                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            {assignment.FormGroup}
                                        </span>
                                    )}
                                    {assignment.GroupName && (
                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            <i className="fas fa-users" style={{ fontSize: '0.6rem' }}></i> {assignment.GroupName}
                                        </span>
                                    )}
                                    {/* LIS Badge */}
                                    <span className="badge bg-primary text-white" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                        <i className="fas fa-microscope" style={{ fontSize: '0.6rem' }}></i> LIS
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Dates & Status (20%) */}
                        <div className="col-2">
                            <div className="d-flex flex-column" style={{ fontSize: '0.7rem', lineHeight: '1.3' }}>
                                <div className="text-muted mb-0">
                                    <i className="fas fa-calendar-plus" style={{ fontSize: '0.65rem', width: '12px' }}></i>
                                    <span className="fw-semibold">{formatDate(assignment.ItemCreated)}</span>
                                </div>
                                {(assignment.StartDate || assignment.EndDate) && (
                                    <div className="text-muted mb-0">
                                        <i className="fas fa-calendar-check" style={{ fontSize: '0.65rem', width: '12px' }}></i>
                                        <span>{assignment.StartDate ? formatDate(assignment.StartDate) : '-'}</span>
                                    </div>
                                )}
                                {assignment.EndDate && (
                                    <div className="text-muted mb-0">
                                        <i className="fas fa-calendar-times" style={{ fontSize: '0.65rem', width: '12px' }}></i>
                                        <span>{formatDate(assignment.EndDate)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Column 3: Workflow & Validation (20%) */}
                        <div className="col-2">
                            <div className="d-flex flex-column gap-1">
                                {assignment.workflowInfo && assignment.workflowInfo.hasWorkflow && (
                                    <div className="d-flex align-items-center gap-1">
                                        <span className={`badge ${assignment.workflowInfo.isCurrentActor ? 'bg-dark' : 'bg-light border'} ${assignment.workflowInfo.isCurrentActor ? 'text-white' : 'text-dark'}`}
                                              style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            <i className="fas fa-users-cog" style={{ fontSize: '0.6rem' }}></i>
                                            {assignment.workflowInfo.currentActorOrder}/{assignment.workflowInfo.totalActors}
                                        </span>
                                        {assignment.workflowInfo.isCurrentActor && (
                                            <span className="badge bg-dark text-white" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                                <i className="fas fa-hand-point-right"></i> {t('yourTurn', { ns: 'labels' })}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {assignment.ToValidate && (
                                    <div className="d-flex align-items-center">
                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                            <i className="fas fa-shield-alt" style={{ fontSize: '0.6rem' }}></i> {t('requiresValidation', { ns: 'labels' })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Column 4: Cycles Info (25%) */}
                        <div className="col-3">
                            <div className="d-flex gap-1 justify-content-center">
                                <div className="text-center px-2 py-1 bg-light border rounded"
                                     style={{ minWidth: '60px', flex: '1' }}>
                                    <div className="fw-bold text-dark" style={{ fontSize: '1rem', lineHeight: '1' }}>
                                        {assignment.completedCycles}
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', lineHeight: '1' }}>
                                        {t('completedCycles', { ns: 'labels' })}
                                    </small>
                                </div>
                                <div className="text-center px-2 py-1 bg-light border rounded"
                                     style={{ minWidth: '60px', flex: '1' }}>
                                    <div className="fw-bold text-dark" style={{ fontSize: '1rem', lineHeight: '1' }}>
                                        {assignment.remainingCycles}
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', lineHeight: '1' }}>
                                        {t('remainingCycles', { ns: 'labels' })}
                                    </small>
                                </div>
                                <div className="text-center px-2 py-1 bg-light border rounded"
                                     style={{ minWidth: '60px', flex: '1' }}>
                                    <div className="fw-bold text-dark" style={{ fontSize: '1rem', lineHeight: '1' }}>
                                        {assignment.totalCycles}
                                    </div>
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem', lineHeight: '1' }}>
                                        {t('totalCycles', { ns: 'labels' })}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Column 5: Actions (10%) */}
                        <div className="col-2 text-end">
                            <div className="d-flex flex-column gap-1 align-items-end">
                                {assignment.Status === 'Active' &&
                                 assignment.remainingCycles > 0 &&
                                 (!assignment.workflowInfo || assignment.workflowInfo.canCreateNew !== false) && (
                                    <button
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={() => handleCompileForm(assignment)}
                                        style={{ fontSize: '0.7rem', padding: '3px 8px' }}
                                        title={t('compileForm', { ns: 'labels' })}
                                    >
                                        <i className="fa-solid fa-pen-to-square"></i> {t('compileForm', { ns: 'labels' })}
                                    </button>
                                )}
                                <button
                                    className="btn btn-sm btn-outline-secondary w-100"
                                    onClick={() => setExpanded(!expanded)}
                                    style={{ fontSize: '0.7rem', padding: '3px 8px' }}
                                >
                                    <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} me-1`}></i>
                                    {t('formCompletions', { ns: 'labels' })} ({assignment.completions?.length || 0})
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Expanded Completions Section */}
                    {expanded && (
                        <div className="mt-2 pt-2 border-top">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <h6 className="text-secondary mb-0" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                    <i className="fas fa-list-ul me-1"></i>
                                    {t('formCompletions', { ns: 'labels' })} ({assignment.completions?.length || 0})
                                </h6>
                            </div>

                            {assignment.completions && assignment.completions.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.7rem' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <tr>
                                                <th style={{ width: '4%', padding: '4px 8px' }}>#</th>
                                                <th style={{ width: '12%', padding: '4px 8px' }}>{t('Status', { ns: 'labels' })}</th>
                                                <th style={{ width: '18%', padding: '4px 8px' }}>{t('Compiler', { ns: 'labels' })}</th>
                                                <th style={{ width: '15%', padding: '4px 8px' }}>{t('CreatedDate', { ns: 'labels' })}</th>
                                                <th style={{ width: '36%', padding: '4px 8px' }}>{t('details', { ns: 'labels' })}</th>
                                                <th style={{ width: '15%', padding: '4px 8px' }} className="text-end">{t('actions', { ns: 'labels' })}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignment.completions.map((completion, index) => (
                                                <tr
                                                    key={completion.Id}
                                                    style={{
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.15s',
                                                        backgroundColor: completion.countsAsCycle ? '#f8f9fa' : 'transparent'
                                                    }}
                                                    onClick={() => handleViewCompletion(assignment, completion)}
                                                >
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <span className={`badge ${completion.countsAsCycle ? 'bg-dark text-white' : 'bg-light text-dark border'}`}
                                                              style={{ fontSize: '0.65rem' }}>
                                                            {index + 1}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <span className={`badge ${getStatusBadgeColor(completion.Status)}`}
                                                              style={{ fontSize: '0.65rem' }}>
                                                            {t(completion.Status, { ns: 'labels' })}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <i className="fas fa-user me-1 text-muted" style={{ fontSize: '0.65rem' }}></i>
                                                        <span style={{ fontSize: '0.7rem' }}>{completion.Compiler}</span>
                                                    </td>
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <i className="fas fa-clock me-1 text-muted" style={{ fontSize: '0.65rem' }}></i>
                                                        <span style={{ fontSize: '0.7rem' }}>{formatDateTime(completion.ItemCreated)}</span>
                                                    </td>
                                                    <td style={{ padding: '4px 8px' }}>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {completion.countsAsCycle && (
                                                                <span className="badge bg-dark text-white" style={{ fontSize: '0.6rem' }}>
                                                                    <i className="fas fa-check-circle"></i> {t('completedCycle', { ns: 'labels' })}
                                                                </span>
                                                            )}
                                                            {completion.workflowCompletionInfo && completion.workflowCompletionInfo.hasWorkflow && (
                                                                <>
                                                                    {completion.workflowCompletionInfo.needsUserAction && (
                                                                        <span className="badge bg-dark text-white" style={{ fontSize: '0.6rem' }}>
                                                                            <i className="fas fa-exclamation-triangle"></i> {t('needsYourAction', { ns: 'labels' })}
                                                                        </span>
                                                                    )}
                                                                    {completion.workflowCompletionInfo.userHasCompleted && (
                                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                                            <i className="fas fa-user-check"></i> {t('yourPartCompleted', { ns: 'labels' })}
                                                                        </span>
                                                                    )}
                                                                    {completion.workflowCompletionInfo.waitingForOthers && !completion.workflowCompletionInfo.userHasCompleted && (
                                                                        <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                                            <i className="fas fa-hourglass-half"></i> {t('waiting', { ns: 'labels' })}
                                                                        </span>
                                                                    )}
                                                                    <span className="badge bg-light text-dark border" style={{ fontSize: '0.6rem' }}>
                                                                        <i className="fas fa-layer-group"></i> {completion.workflowCompletionInfo.currentActorOrder}/{completion.workflowCompletionInfo.totalActors}
                                                                    </span>
                                                                </>
                                                            )}
                                                            {completion.ToValidate && completion.Validated && (
                                                                <span className="badge bg-dark text-white" style={{ fontSize: '0.6rem' }}>
                                                                    <i className="fas fa-stamp"></i> {t('validated', { ns: 'labels' })}
                                                                    {completion.ValidatedBy && <small className="ms-1">({completion.ValidatedBy})</small>}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-end" style={{ padding: '4px 8px' }}>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleViewCompletion(assignment, completion);
                                                            }}
                                                            style={{ fontSize: '0.65rem', padding: '2px 8px' }}
                                                            title={t('view', { ns: 'labels' })}
                                                        >
                                                            <i className="fas fa-eye"></i> {t('view', { ns: 'labels' })}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-muted py-2 bg-light rounded" style={{ fontSize: '0.75rem' }}>
                                    <i className="fas fa-inbox fa-lg mb-1 opacity-50"></i>
                                    <div>{t('noCompletionsYet', { ns: 'labels' })}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // If viewing a form, show the Form_Compiler in read-only mode
    if (viewingForm && viewingFormData) {
        return (
            <div className="container-fluid">
                <Form_Compiler
                    form={viewingFormData.form}
                    formCompletion={viewingFormData}
                    language="it"
                    readOnly={isReadOnly}
                    onClose={handleCloseFormViewer}
                    colors={{
                        primary: '#f05f23',
                        primaryHover: '#d44d12',
                        primaryLight: '#fef0ea',
                        secondary: '#6c757d',
                        secondaryLight: '#e9ecef',
                        dark: '#343a40',
                        light: '#f8f9fa',
                        white: '#ffffff',
                        success: '#28a745',
                        danger: '#dc3545',
                        warning: '#ffc107',
                        info: '#17a2b8'
                    }}
                />
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ height: `calc(100vh - ${headerHeight}px)` }}>
            {/* Fixed Header */}
            <header className="bg-white shadow-sm" style={{ position: 'fixed', top: `${headerHeight}px`, left: '240px', right: 0, zIndex: 1 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    {/* Breadcrumb */}
                    <ol className="breadcrumb mb-0"></ol>
                    
                    {/* Action buttons */}
                    <div className="d-flex gap-3">
                        <a 
                            className="text-primary fs-13" 
                            href="/form_builder/finder" 
                            role="button" 
                            title={t('searchForms', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-magnifying-glass fa-2x"></i>
                        </a>
                        <a 
                            className="text-primary fs-13" 
                            onClick={() => loadDashboardData(activeTab)}
                            role="button" 
                            title={t('refresh', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-sync fa-2x"></i>
                        </a>
                    </div>
                </div>
            </header>

            {/* Scrollable Content */}
            <main className="overflow-auto" style={{ paddingTop: '60px', height: '100%', backgroundColor: '#f8f9fa' }}>
                <div className="container-fluid p-2">
                    <div className="row g-0">
                        <div className="col-12">
                            {/* Compact Dashboard Header */}
                            <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                                <div>
                                    <h5 className="mb-0 fw-bold text-dark">
                                        <i className="fas fa-microscope me-2"></i>
                                        {t('lisChecklistDashboard', { ns: 'labels', defaultValue: 'Dashboard Checklist LIS' })}
                                    </h5>
                                    <small className="text-muted">
                                        {t('lisChecklistDashboardDescription', { ns: 'labels', defaultValue: 'Checklist di controllo delle infezioni dal flusso LIS' })}
                                    </small>
                                </div>
                                {/* Compact Tabs */}
                                <div className="btn-group btn-group-sm" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${activeTab === 'Active' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleTabChange('Active')}
                                        style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                                    >
                                        <i className="fas fa-clock me-1" style={{ fontSize: '0.75rem' }}></i>
                                        Attive
                                        {assignments && (
                                            <span className={`badge ms-1 ${activeTab === 'Active' ? 'bg-light text-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                {assignments.filter(a => a.Status === 'Active').length}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${activeTab === 'Completed' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleTabChange('Completed')}
                                        style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                                    >
                                        <i className="fas fa-check-circle me-1" style={{ fontSize: '0.75rem' }}></i>
                                        Completate
                                        {assignments && (
                                            <span className={`badge ms-1 ${activeTab === 'Completed' ? 'bg-light text-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                {assignments.filter(a => a.Status === 'Completed').length}
                                            </span>
                                        )}
                                    </button>
                                    {/* <button
                                        type="button"
                                        className={`btn ${activeTab === 'Approved' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleTabChange('Approved')}
                                        style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                                    >
                                        <i className="fas fa-thumbs-up me-1" style={{ fontSize: '0.75rem' }}></i>
                                        Approvate
                                        {assignments && (
                                            <span className={`badge ms-1 ${activeTab === 'Approved' ? 'bg-light text-primary' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                {assignments.filter(a => a.Status === 'Approved').length}
                                            </span>
                                        )}
                                    </button> */}
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="px-2">
                                {loading && (
                                    <div className="d-flex justify-content-center py-4">
                                        <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                            <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div className="alert alert-danger alert-dismissible fade show mb-2" role="alert" style={{ fontSize: '0.85rem', padding: '8px 12px' }}>
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <strong>{t('error', { ns: 'labels' })}:</strong> {error}
                                    </div>
                                )}

                                {!loading && !error && (
                                    <>
                                        {getFilteredAssignments().length > 0 ? (
                                            <div>
                                                {getFilteredAssignments().map((assignment) => (
                                                    <AssignmentCard
                                                        key={assignment.Id}
                                                        assignment={assignment}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 bg-white border rounded">
                                                <i className={`fas ${activeTab === 'Active' ? 'fa-clock' : activeTab === 'Completed' ? 'fa-check-circle' : 'fa-thumbs-up'} fa-2x text-muted mb-2`} style={{ opacity: 0.3 }}></i>
                                                <h6 className="text-muted mb-1">
                                                    {activeTab === 'Active'
                                                        ? t('noActiveAssignments', { ns: 'labels' })
                                                        : activeTab === 'Completed'
                                                        ? t('noCompletedAssignments', { ns: 'labels' })
                                                        : t('noApprovedAssignments', { ns: 'labels', defaultValue: 'Nessuna assegnazione approvata' })
                                                    }
                                                </h6>
                                                <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                                                    {activeTab === 'Active'
                                                        ? t('noActiveAssignmentsDescription', { ns: 'labels' })
                                                        : activeTab === 'Completed'
                                                        ? t('noCompletedAssignmentsDescription', { ns: 'labels' })
                                                        : t('noApprovedAssignmentsDescription', { ns: 'labels', defaultValue: 'Non ci sono assegnazioni approvate da visualizzare.' })
                                                    }
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LIS_Checklist_Dashboard;
